import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ReplyNotifications } from './ReplyNotifications';
import { NotificationBell } from './NotificationBell';
import CommandPalette from './CommandPalette';
import OfficeSwitcher from './OfficeSwitcher';
import { useCompany } from '../contexts/CompanyContext';
import { DisputeBanner } from './disputes/DisputeBanner';
import { GraceCountdownChip } from './disputes/GraceCountdownChip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { HelpAssistant } from './help/HelpAssistant';
import {
  LayoutDashboard,
  LifeBuoy,
  HelpCircle,
  Sun,
  Truck,
  Users,
  Building2,
  FileText,
  Upload,
  Download,
  Receipt,
  Settings,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Plug,
  Calculator,
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  DollarSign,
  Mail,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Wallet,
  Shield,
  CreditCard,
  Activity,
  CheckSquare,
  Send,
  Trophy,
  Network,
  CalendarClock,
  Brain,
  Gauge,
  ClipboardList as ActivityIcon,
  FileUp,
  Copy,
  ExternalLink,
  AlertTriangle,
  Clock,
  FileSpreadsheet,
  Banknote,
  Sparkles,
  FileSearch,
  Flag,
  ShieldCheck,
  HeartPulse,
  MailX,
  ArrowRightLeft,
  Bell,
  MailWarning,
  ListChecks,
  Tag,
} from 'lucide-react';
import { signInToLoadBoard } from '../lib/loadboardSso';

// Role definitions matching backend
const ROLE_LABELS = {
  'admin': 'Administrator',
  'sales': 'Sales Rep',
  'accounting': 'Accounting Manager',
  'office_manager': 'Office Manager'
};

// Role-based navigation restrictions
// Administrator: Full access - admin, sales rep, and dispatcher combined
// Sales: Sales rep and dispatcher - manages own customers and loads
// Accounting: Accounting functions and reports only

const canAccessAccounting = (role) => ['admin', 'accounting', 'office_manager'].includes(role);

// Email Hub is admin-first (owns marketing templates + AI campaigns).
// Non-admin OMs can be granted access via ``manager_permissions.access_email_hub``.
const canAccessEmailHub = (user) => {
  if (!user) return false;
  if (['admin', 'accounting'].includes(user.role)) return true;
  if (user.role === 'office_manager') {
    return Boolean(user.manager_permissions?.access_email_hub);
  }
  return false;
};
const canAccessReports = (role) => ['admin', 'accounting', 'office_manager'].includes(role);
const isAdmin = (role) => role === 'admin';
const isAdminOrAccounting = (role) => ['admin', 'accounting'].includes(role);
const isAdminOrManager = (role) => ['admin', 'accounting', 'office_manager'].includes(role);
const canViewAllData = (role) => ['admin', 'accounting'].includes(role);
const canManageUsers = (role) => ['admin', 'accounting', 'office_manager'].includes(role);

export const Layout = ({ children }) => {
  const { user, logout, token, API, isImpersonating } = useAuth();
  const company = useCompany();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState({ customers: 0, carriers: 0, rate_changes: 0, total: 0 });
  const [workflowAlertCount, setWorkflowAlertCount] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState({
    operations: true,
    crm: true,
    network: true,
    finance: true,
    admin: true
  });

  // Fetch pending approvals for roles that can manage approvals
  useEffect(() => {
    const fetchPendingApprovals = async () => {
      if (!isAdminOrManager(user?.role)) return;
      
      try {
        const response = await axios.get(`${API}/approvals/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingApprovals(response.data);
      } catch (error) {
        // Notification count unavailable
      }
    };

    fetchPendingApprovals();
    const interval = setInterval(fetchPendingApprovals, 30000);
    return () => clearInterval(interval);
  }, [user?.role, API, token]);

  // Fetch Action Center alert count — surfaces the "this needs attention"
  // number directly in the sidebar so the page stops feeling useless.
  useEffect(() => {
    const fetchWorkflowAlerts = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/workflow/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWorkflowAlertCount((response.data?.alerts || []).length);
      } catch (_) {
        // Silently ignore — endpoint may 403 for some roles.
      }
    };
    fetchWorkflowAlerts();
    const id = setInterval(fetchWorkflowAlerts, 60000);
    return () => clearInterval(id);
  }, [user, API, token]);

  // Grouped navigation structure
  const navigationGroups = [
    {
      id: 'operations',
      name: 'Operations',
      icon: Briefcase,
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard },
        // Action Center surfaces every alert the system raises (dormant
        // customers, stale leads, expiring insurance, overdue collections).
        // The live alert count badge makes it obvious there's something to
        // act on — otherwise users glance past it.
        {
          name: 'Action Center',
          href: '/workflow',
          icon: Activity,
          badge: workflowAlertCount,
        },
        { name: 'Loads', href: '/loads', icon: Truck },
        { name: 'Opportunities', href: '/opportunities', icon: Mail },
      ]
    },
    {
      id: 'crm',
      name: 'CRM',
      icon: Users,
      items: [
        // My Day is the rep's start-of-shift screen: who to call now,
        // measured against the daily outreach goals.
        { name: 'My Day', href: '/my-day', icon: Sun },
        { name: 'Tasks Dashboard', href: '/tasks', icon: CheckSquare },
        // Managers only: every open task on the team, grouped by owner.
        { name: 'Team Tasks', href: '/team-tasks', icon: ClipboardList,
          check: () => isAdminOrManager(user?.role) },
        { name: 'Leads', href: '/leads', icon: Users },
        { name: 'Customers', href: '/customers', icon: Building2 },
        { name: 'Carriers', href: '/carriers', icon: Truck },
        { name: 'Credit Requests', href: '/credit-requests', icon: CreditCard },
        // Single consolidated "Email" entry — folded from the previous
        // pair (``Email Analytics`` + ``Email Hub``) as part of the
        // Feb-2026 hub-navigation refactor. Now that every page in the
        // Email cluster (Hub / Sequences / Campaigns / Analytics /
        // Suppressions / AI Command) shares a sticky ``EmailHubNavTabs``
        // bar at the top, one sidebar entry is enough — users cross
        // between tools via the tab bar, not the sidebar.
        //
        // Admins / accounting always land on the full Email Hub. Office
        // managers land on Email Hub only when granted the
        // ``access_email_hub`` flag; otherwise they land on the more
        // limited Email Analytics view scoped to their own sent emails.
        {
          name: 'Email',
          href: canAccessEmailHub(user) ? '/admin/email-hub' : '/email-analytics',
          icon: Mail,
        },
      ]
    },
    {
      id: 'finance',
      name: 'Finance',
      icon: Wallet,
      check: canAccessAccounting,
      items: [
        { name: 'Invoices', href: '/invoices', icon: Receipt },
        // Accounting is the tabbed hub containing Insights, Aging, WIP,
        // Billing, AP Reconciliation, and Collections. Margins is kept
        // one-click top-level per user's daily-driver need.
        { name: 'Accounting', href: '/accounting', icon: Calculator },
        { name: 'Margins', href: '/accounting/profit-aggregations', icon: TrendingUp, check: isAdminOrAccounting },
        { name: 'Commissions', href: '/commissions', icon: DollarSign },
        // Payments Register — cash actually in/out for a period (MTD, last
        // month, custom). The AR/AP tabs answer "what's owed"; this answers
        // "what moved".
        { name: 'Payments Register', href: '/finance/payments', icon: Banknote, check: isAdminOrAccounting },
        // Reports & Exports groups QuickBooks Export, 1099-NEC and
        // Scheduled Reports. Rarely-used monthly / annual chores that
        // don't need their own sidebar slots.
        { name: 'Reports & Exports', href: '/finance/reports-exports', icon: FileSpreadsheet, check: isAdminOrAccounting },
      ]
    },
    {
      id: 'admin',
      name: 'Admin',
      icon: Shield,
      items: [
        { name: 'Approvals', href: '/approvals', icon: ClipboardCheck, check: isAdminOrManager, badge: pendingApprovals.total },
        { name: 'Users', href: '/users', icon: Users, check: canManageUsers },
        { name: 'Reports', href: '/reports', icon: BarChart3, check: canAccessReports },
        // System Health groups the "one-off admin utilities" that a
        // dispatcher / accountant hits once a month at most: Loads Audit,
        // Duplicate Loads, Sales Rep Health, Credit Risk, Open Disputes.
        // Each still has its own route for deep-linking.
        { name: 'System Health', href: '/admin/system-health', icon: HeartPulse, check: isAdminOrManager },
        { name: 'Data Health Console', href: '/admin/data-health', icon: ShieldCheck, check: isAdminOrManager },
        { name: 'Duplicate Customers', href: '/admin/customer-duplicates', icon: Users, check: isAdmin },
        // Settings is a tabbed hub containing Offices, Document Templates,
        // Integrations, Data Import, Entity Categories, Grace Periods,
        // Factoring, plus core company / user settings.
        { name: 'Settings', href: '/settings', icon: Settings },
        { name: 'Notifications', href: '/notifications', icon: Bell, check: () => user?.role === 'admin' },
        // Notifications Center — Feb 2026, central inventory + kill-switch
        // for every automated email the platform sends. Admin only.
        { name: 'Notifications Center', href: '/admin/notifications-center', icon: Bell, check: () => user?.role === 'admin' },
        { name: 'Email Health', href: '/admin/email-health', icon: MailWarning, check: () => isAdminOrAccounting(user?.role) },
        // Lead List Cleanup — Jun 2026, retires dead/malformed lead addresses
        // before a bulk send bounces off them.
        { name: 'Lead List Cleanup', href: '/admin/lead-cleanup', icon: ListChecks, check: () => isAdminOrAccounting(user?.role) },
      ]
    }
  ];

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Filter groups and items based on role
  const getFilteredGroups = () => {
    return navigationGroups
      .filter(group => !group.check || group.check(user?.role))
      .map(group => ({
        ...group,
        items: group.items.filter(item => !item.check || item.check(user?.role))
      }))
      .filter(group => group.items.length > 0);
  };

  const filteredGroups = getFilteredGroups();

  // Find current page name for header
  const getCurrentPageName = () => {
    for (const group of navigationGroups) {
      for (const item of group.items) {
        if (item.href === location.pathname) {
          return item.name;
        }
      }
    }
    // Pages reachable from Settings rather than the sidebar
    const extras = {
      '/admin/mailbox-health': 'Mailbox Health',
      '/admin/email-pipeline-health': 'Email Pipeline Health',
      '/admin/ownership-transfers': 'Ownership Transfers',
      '/admin/security-log': 'Security Log',
    };
    return extras[location.pathname] || 'TMS';
  };

  return (
    <div className={`flex h-screen bg-slate-100 ${isImpersonating ? 'pt-12' : ''}`}>
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-slate-900 transition-all duration-200 z-50 ${sidebarOpen ? 'w-56' : 'w-0 md:w-14'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-14 flex items-center justify-between px-3 border-b border-slate-800">
            {sidebarOpen && (
              <Link to="/dashboard" className="flex items-center">
                <img src="/logo-sidebar.png" alt={company.company_name || 'TMS'} className="h-8 object-contain" />
              </Link>
            )}
            {!sidebarOpen && (
              <Link to="/dashboard" className="mx-auto">
                <img src="/logo-icon.png" alt={company.company_name || 'TMS'} className="h-7 w-7 rounded object-contain" />
              </Link>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-800 transition-colors"
              data-testid="sidebar-toggle-btn"
            >
              {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-3 overflow-y-auto">
            {filteredGroups.map((group) => {
              const GroupIcon = group.icon;
              const isExpanded = expandedGroups[group.id];
              const hasActiveItem = group.items.some(item => location.pathname === item.href);
              
              return (
                <div key={group.id} className="mb-1">
                  {/* Group Header */}
                  {sidebarOpen ? (
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        hasActiveItem ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <GroupIcon className="h-3.5 w-3.5" />
                        {group.name}
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                    </button>
                  ) : (
                    <div className="px-2 py-1">
                      <div className="h-px bg-slate-800" />
                    </div>
                  )}
                  
                  {/* Group Items */}
                  {(isExpanded || !sidebarOpen) && (
                    <div className={sidebarOpen ? 'ml-2' : ''}>
                      {group.items.map((item) => {
                        const isActive = location.pathname === item.href;
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                            className={`flex items-center gap-2.5 mx-2 px-2.5 py-2 rounded-md text-sm transition-colors relative ${
                              isActive
                                ? 'bg-slate-800 text-white font-medium'
                                : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                            }`}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                            {sidebarOpen && (
                              <>
                                <span className="flex-1 truncate">{item.name}</span>
                                {item.badge > 0 && (
                                  <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                                    {item.badge}
                                  </span>
                                )}
                              </>
                            )}
                            {!sidebarOpen && item.badge > 0 && (
                              <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-3.5 w-3.5 text-[9px] font-bold text-white bg-red-500 rounded-full">
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-slate-800">
            {sidebarOpen ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2.5 px-2">
                  <div className="h-8 w-8 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{ROLE_LABELS[user?.role] || user?.role}</p>
                  </div>
                </div>
                <button
                  onClick={() => signInToLoadBoard({ API, token })}
                  data-testid="loadboard-signin-btn"
                  className="flex items-center gap-2 w-full px-2.5 py-2 text-sm text-blue-300 hover:bg-blue-900/40 hover:text-blue-100 rounded-md transition-colors"
                  title="Open redballxpress.us/Load-Board signed in as you"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Sign in to Load Board</span>
                </button>
                <Link
                  to="/help"
                  data-testid="sidebar-help-center-link"
                  className="flex items-center gap-2 w-full px-2.5 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
                  title="Guides for every part of the TMS, plus an AI assistant"
                >
                  <LifeBuoy className="h-4 w-4" />
                  <span>Help Center</span>
                </Link>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="flex items-center gap-2 w-full px-2.5 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <Link
                  to="/help"
                  data-testid="sidebar-help-center-icon-link"
                  className="flex items-center justify-center w-full p-2 text-slate-300 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
                  title="Help Center"
                >
                  <LifeBuoy className="h-4 w-4" />
                </Link>
                <button
                  onClick={() => signInToLoadBoard({ API, token })}
                  data-testid="loadboard-signin-icon-btn"
                  className="flex items-center justify-center w-full p-2 text-blue-300 hover:bg-blue-900/40 hover:text-blue-100 rounded-md transition-colors"
                  title="Sign in to Load Board"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  onClick={logout}
                  data-testid="logout-icon-btn"
                  className="flex items-center justify-center w-full p-2 text-slate-400 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ${sidebarOpen ? 'md:ml-56' : 'md:ml-14'}`}>
        {/* Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 md:px-6 sticky top-0 z-40">
          <div className="flex-1">
            <h1 className="text-lg font-bold tracking-tight text-slate-900">
              {getCurrentPageName()}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Quick search / command palette hint — triggers the same
                ⌘K / Ctrl+K listener registered by <CommandPalette>. */}
            <button
              type="button"
              onClick={() => {
                const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
                window.dispatchEvent(new KeyboardEvent('keydown', {
                  key: 'k', code: 'KeyK', bubbles: true,
                  ctrlKey: !isMac, metaKey: isMac,
                }));
              }}
              data-testid="command-palette-open-btn"
              title="Search pages (Ctrl+K)"
              className="hidden md:inline-flex items-center gap-2 h-8 px-3 text-xs text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
            >
              <span>Search…</span>
              <kbd className="ml-1 px-1.5 py-0.5 text-[10px] font-mono text-slate-500 bg-white border border-slate-300 rounded">⌘K</kbd>
            </button>
            {/* Ask-the-TMS assistant, reachable from every screen */}
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              data-testid="topbar-help-btn"
              title="Ask how to do something in the TMS"
              className="inline-flex items-center justify-center h-8 w-8 text-slate-500 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            {/* Multi-office manager: filter data down to one office */}
            <OfficeSwitcher />
            {/* Grace-period countdown — only shown when active */}
            <GraceCountdownChip />
            {/* Email Reply Notifications */}
            <ReplyNotifications />
            {/* Assigned tasks + approvals */}
            <NotificationBell pending={pendingApprovals} userRole={user?.role} />
            {/* User Info */}
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-[11px] text-slate-500">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
              <div className="h-8 w-8 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <button
                onClick={() => signInToLoadBoard({ API, token })}
                data-testid="loadboard-signin-header-btn"
                title="Open Load Board signed in as you"
                className="ml-1 inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden md:inline">Load Board</span>
              </button>
            </div>
          </div>
        </header>

        {/* Global dispute warning banner — only renders when the current
            sales rep has 1+ disputed loads. Stays at the top of every
            page (above scrollable content) so it can't be missed. */}
        <DisputeBanner />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>

      {/* ⌘K / Ctrl+K command palette — mounted once at the layout root so
          the global keyboard shortcut works from anywhere in the app. */}
      <CommandPalette />
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="topbar-help-dialog">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="text-base">Ask the TMS</DialogTitle>
          </DialogHeader>
          <div className="px-5 pb-5">
            <HelpAssistant compact />
            <Link
              to="/help"
              onClick={() => setHelpOpen(false)}
              className="mt-3 inline-block text-xs text-slate-500 hover:text-slate-900"
              data-testid="help-dialog-browse-link"
            >
              Browse all guides →
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
